import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Collapse from '@mui/material/Collapse';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import api from '../api';

const reais = (valor) => Number(valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const numero = (texto) => Number(String(texto || '').replace(/\./g, '').replace(',', '.'));

/**
 * Cobrança extra (Administração › Financeiro): taxa com descrição e valor próprios para os mensalistas, sem
 * mexer na mensalidade. Mostra quantos serão cobrados e o total antes de confirmar. API: /financeiro/cobranca-extra.
 */
const CobrancaExtraDialog = ({ open, onClose, onConcluido, onMessage }) => {
  const [descricao, setDescricao] = useState('');
  const [valor, setValor] = useState('');
  const [incluirIsentos, setIncluirIsentos] = useState(false);
  const [incluirDiaristas, setIncluirDiaristas] = useState(false);
  const [previa, setPrevia] = useState(null);
  const [verLista, setVerLista] = useState(false);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDescricao('');
    setValor('');
    setIncluirIsentos(false);
    setIncluirDiaristas(false);
    setVerLista(false);
  }, [open]);

  // Prévia: quem será cobrado com as opções escolhidas
  useEffect(() => {
    if (!open) return undefined;
    let cancelado = false;
    api
      .get(`financeiro/cobranca-extra/previa?incluirIsentos=${incluirIsentos}&incluirDiaristas=${incluirDiaristas}`)
      .then(({ data }) => !cancelado && setPrevia(data))
      .catch(() => !cancelado && setPrevia(null));
    return () => {
      cancelado = true;
    };
  }, [open, incluirIsentos, incluirDiaristas]);

  const valorNumero = numero(valor);
  const valido = descricao.trim().length >= 3 && valorNumero > 0;
  const quantidade = previa ? previa.quantidade : 0;

  const gerar = async () => {
    setEnviando(true);
    try {
      const { data } = await api.post('financeiro/cobranca-extra', { descricao: descricao.trim(), valor: valorNumero, incluirIsentos, incluirDiaristas });
      const saldo = data.pagasComSaldo ? ` (${data.pagasComSaldo} pagas com saldo)` : '';
      if (onMessage) onMessage(`Cobrança extra gerada para ${data.quantidade} jogadores · total ${reais(data.total)}${saldo}.`, 'success');
      onClose();
      if (onConcluido) onConcluido();
    } catch (error) {
      if (onMessage) onMessage(error.response?.data?.error || 'Não foi possível gerar a cobrança extra.', 'error');
    }
    setEnviando(false);
  };

  return (
    <Dialog open={open} onClose={enviando ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Cobrança extra</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Lança uma taxa no extrato de cada jogador, com PIX e aviso por e-mail e no app. Não altera a mensalidade.
          </Typography>
          <TextField
            label="Descrição"
            placeholder="Ex.: Taxa da festa de fim de ano"
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            inputProps={{ maxLength: 60 }}
            helperText='Aparece no extrato como "Cobrança extra - <descrição>"'
            fullWidth
            autoFocus
          />
          <TextField
            label="Valor por jogador"
            value={valor}
            onChange={(e) => setValor(e.target.value.replace(/[^\d,.]/g, ''))}
            InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
            inputProps={{ inputMode: 'decimal' }}
            fullWidth
          />
          <Stack>
            <FormControlLabel control={<Switch checked={incluirIsentos} onChange={(e) => setIncluirIsentos(e.target.checked)} />} label="Incluir mensalistas isentos de mensalidade" />
            <FormControlLabel control={<Switch checked={incluirDiaristas} onChange={(e) => setIncluirDiaristas(e.target.checked)} />} label="Incluir diaristas" />
          </Stack>
          {previa ? (
            <Alert severity="info" action={quantidade > 0 && <Button size="small" color="inherit" onClick={() => setVerLista((v) => !v)}>{verLista ? 'Ocultar' : 'Ver lista'}</Button>}>
              Serão cobrados <b>{quantidade}</b> jogadores
              {valorNumero > 0 && <> · total <b>{reais(valorNumero * quantidade)}</b></>}.
              Quem tem saldo positivo que cobre o valor tem a cobrança descontada do saldo.
            </Alert>
          ) : (
            <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={16} /><Typography variant="body2">Calculando...</Typography></Stack>
          )}
          <Collapse in={verLista}>
            <Typography variant="body2" color="text.secondary" sx={{ maxHeight: 180, overflowY: 'auto' }}>
              {(previa?.jogadores || []).map((j) => `${j.nome}${j.tipo === 'D' ? ' (diarista)' : ''}${j.isento ? ' (isento)' : ''}`).join(' · ')}
            </Typography>
          </Collapse>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={enviando}>Cancelar</Button>
        <Button variant="contained" onClick={gerar} disabled={!valido || !quantidade || enviando}>
          {enviando ? 'Gerando...' : `Cobrar ${quantidade} jogadores`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CobrancaExtraDialog;
